import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { MeFeedAuthor, MeFeedEntityType } from '../lib/api';

export type SubscribeModalTarget =
  | { mode: 'personal' }
  | { mode: 'team'; teamId: string; teamName: string; defaultSeats?: number };
export type SubscriptionModalTarget = { mode: 'personal' } | { mode: 'team'; teamId: string; teamName: string };

type UIStore = {
  isTaskModalOpen: boolean;
  isAgentConfigModalOpen: boolean;
  isSubscribeModalOpen: boolean;
  subscribeModalTarget: SubscribeModalTarget;
  isSubscriptionModalOpen: boolean;
  subscriptionModalTarget: SubscriptionModalTarget;
  agentConfigFiltersByProjectId: Record<string, AgentConfigFilters>;
  homeActivityFilters: HomeActivityFilters;
  lastRunnerSelectionByProjectId: Record<string, LastRunnerSelection>;
  openTaskModal: () => void;
  closeTaskModal: () => void;
  openAgentConfigModal: () => void;
  closeAgentConfigModal: () => void;
  openSubscribeModal: (target?: SubscribeModalTarget) => void;
  closeSubscribeModal: () => void;
  openSubscriptionModal: (target?: SubscriptionModalTarget) => void;
  closeSubscriptionModal: () => void;
  setAgentConfigShowDeleted: (projectId: string, value: boolean) => void;
  setAgentConfigShowAllAgents: (projectId: string, value: boolean) => void;
  setHomeActivityFilters: (value: HomeActivityFilters) => void;
  resetHomeActivityFilters: () => void;
  setLastRunnerSelection: (projectId: string, runnerType: string, model: string) => void;
};

export type AgentConfigFilters = {
  showDeleted: boolean;
  showAllAgents: boolean;
};

export type LastRunnerSelection = {
  runnerType: string;
  model: string;
};

export type HomeActivityStatusByEntity = Record<MeFeedEntityType, string[]>;

export type HomeActivityFilters = {
  entity: MeFeedEntityType[];
  author: MeFeedAuthor;
  statusByEntity: HomeActivityStatusByEntity;
  projects: string[];
};

type PersistedUIState = {
  agentConfigFiltersByProjectId: Record<string, AgentConfigFilters>;
  homeActivityFilters: HomeActivityFilters;
  lastRunnerSelectionByProjectId: Record<string, LastRunnerSelection>;
};

export const defaultAgentConfigFilters: AgentConfigFilters = {
  showDeleted: false,
  showAllAgents: false,
};

export const defaultHomeActivityFilters: HomeActivityFilters = {
  entity: [],
  author: 'all',
  statusByEntity: {
    PLAN: [],
    COMPLETION_REPORT: [],
    POST_MORTEM: [],
    CO_ACTION: [],
    CODE_REVIEW: [],
    DOCUMENT: [],
    CONVENTION: [],
    SKILL: [],
  },
  projects: [],
};

const defaultPersistedUIState: PersistedUIState = {
  agentConfigFiltersByProjectId: {},
  homeActivityFilters: defaultHomeActivityFilters,
  lastRunnerSelectionByProjectId: {},
};

const updateAgentConfigFilters = (
  state: UIStore,
  projectId: string,
  updater: (current: AgentConfigFilters) => AgentConfigFilters,
) => {
  if (!projectId) {
    return {};
  }

  return {
    agentConfigFiltersByProjectId: {
      ...state.agentConfigFiltersByProjectId,
      [projectId]: updater(state.agentConfigFiltersByProjectId[projectId] ?? defaultAgentConfigFilters),
    },
  };
};

export const selectAgentConfigFilters = (state: UIStore, projectId: string): AgentConfigFilters =>
  state.agentConfigFiltersByProjectId[projectId] ?? defaultAgentConfigFilters;

export const selectLastRunnerSelection = (state: UIStore, projectId: string): LastRunnerSelection | null =>
  state.lastRunnerSelectionByProjectId[projectId] ?? null;

export const selectHomeActivityFilters = (state: UIStore): HomeActivityFilters => state.homeActivityFilters;

export const migratePersistedUIState = (persistedState: unknown): PersistedUIState => {
  const state = persistedState as Partial<PersistedUIState> | undefined;

  if (!state) {
    return defaultPersistedUIState;
  }

  const persistedHomeActivityFilters = state.homeActivityFilters as Partial<HomeActivityFilters> | undefined;
  const persistedAuthor = persistedHomeActivityFilters?.author;
  const author = persistedAuthor === 'me' || persistedAuthor === 'others' ? persistedAuthor : 'all';

  return {
    ...defaultPersistedUIState,
    agentConfigFiltersByProjectId: Object.fromEntries(
      Object.entries(state.agentConfigFiltersByProjectId ?? {}).map(([projectId, filters]) => [
        projectId,
        {
          ...defaultAgentConfigFilters,
          ...filters,
        },
      ]),
    ),
    homeActivityFilters: {
      entity: persistedHomeActivityFilters?.entity ?? defaultHomeActivityFilters.entity,
      author,
      statusByEntity: {
        ...defaultHomeActivityFilters.statusByEntity,
        ...persistedHomeActivityFilters?.statusByEntity,
      },
      projects: persistedHomeActivityFilters?.projects ?? defaultHomeActivityFilters.projects,
    },
    lastRunnerSelectionByProjectId: state.lastRunnerSelectionByProjectId ?? {},
  };
};

export const useUIStore = create<UIStore>()(
  persist(
    (set) => ({
      isTaskModalOpen: false,
      isAgentConfigModalOpen: false,
      isSubscribeModalOpen: false,
      subscribeModalTarget: { mode: 'personal' },
      isSubscriptionModalOpen: false,
      subscriptionModalTarget: { mode: 'personal' },
      agentConfigFiltersByProjectId: {},
      homeActivityFilters: defaultHomeActivityFilters,
      lastRunnerSelectionByProjectId: {},
      openTaskModal: () => set({ isTaskModalOpen: true }),
      closeTaskModal: () => set({ isTaskModalOpen: false }),
      openAgentConfigModal: () => set({ isAgentConfigModalOpen: true }),
      closeAgentConfigModal: () => set({ isAgentConfigModalOpen: false }),
      openSubscribeModal: (target = { mode: 'personal' }) =>
        set({ isSubscribeModalOpen: true, subscribeModalTarget: target }),
      closeSubscribeModal: () => set({ isSubscribeModalOpen: false }),
      openSubscriptionModal: (target = { mode: 'personal' }) =>
        set({ isSubscriptionModalOpen: true, subscriptionModalTarget: target }),
      closeSubscriptionModal: () => set({ isSubscriptionModalOpen: false }),
      setAgentConfigShowDeleted: (projectId, value) =>
        set((state) => updateAgentConfigFilters(state, projectId, (current) => ({ ...current, showDeleted: value }))),
      setAgentConfigShowAllAgents: (projectId, value) =>
        set((state) => updateAgentConfigFilters(state, projectId, (current) => ({ ...current, showAllAgents: value }))),
      setHomeActivityFilters: (value) => set({ homeActivityFilters: value }),
      resetHomeActivityFilters: () => set({ homeActivityFilters: defaultHomeActivityFilters }),
      setLastRunnerSelection: (projectId, runnerType, model) =>
        set((state) => {
          if (!projectId) return {};
          return {
            lastRunnerSelectionByProjectId: {
              ...state.lastRunnerSelectionByProjectId,
              [projectId]: { runnerType, model },
            },
          };
        }),
    }),
    {
      name: 'ui-store',
      version: 13,
      migrate: migratePersistedUIState,
      partialize: (state) => ({
        agentConfigFiltersByProjectId: state.agentConfigFiltersByProjectId,
        homeActivityFilters: state.homeActivityFilters,
        lastRunnerSelectionByProjectId: state.lastRunnerSelectionByProjectId,
      }),
    },
  ),
);
